// src/VaccinationReport.js
import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TablePagination,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import PrintIcon from "@mui/icons-material/Print";

/**
 * VaccinationReport Component - Displays vaccination status by appointment date
 * Allows filtering by date range and provides printable output with timestamp and page numbers
 */
const VaccinationReport = () => {
  // State variables for date range and data
  const [startDate, setStartDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  ); // Default to today
  const [endDate, setEndDate] = useState(
    format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), "yyyy-MM-dd")
  ); // Default to 14 days from today
  const [vaccinationData, setVaccinationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef();

  // For pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Add print-specific styles to the document head
  useEffect(() => {
    // Create a style element for print-specific styling
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        #print-container, #print-container * {
          visibility: visible;
        }
        #print-container {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        .no-print { 
          display: none !important; 
        }
        .page-footer { 
          position: fixed;
          bottom: 10px;
          width: 100%;
          font-size: 10px;
          color: #777;
        }
        .page-number:after {
          content: counter(page);
        }
        .report-logo {
          position: absolute;
          top: 10px;
          right: 10px;
        }
        @page {
          size: portrait;
          margin: 15mm;
          counter-increment: page;
        }
        table {
          width: 100% !important;
          font-size: 8pt !important;
          table-layout: fixed !important;
          border-collapse: collapse !important;
          page-break-after: always !important;
        }
        thead {
          display: table-header-group !important;
        }
        tbody {
          display: table-row-group !important;
        }
        tr {
          page-break-inside: avoid !important;
          page-break-after: auto !important;
          height: auto !important;
        }
        th, td {
          padding: 2px 4px !important;
          font-size: 8pt !important;
          overflow: visible !important;
          word-wrap: break-word !important;
          white-space: normal !important;
          max-height: none !important;
          vertical-align: top !important;
        }
        #print-container h5 {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        #print-container .MuiBox-root {
          margin-bottom: 10px !important;
        }
        #print-container .MuiTableContainer-root {
          margin-top: 0 !important;
          overflow: visible !important;
        }
        .same-owner-row, .same-owner-row td {
          background-color: inherit !important;
        }
        /* These are critical for printing background colors */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add event listeners for print events
  useEffect(() => {
    const beforePrint = () => {
      setIsPrinting(true);
    };
    
    const afterPrint = () => {
      setIsPrinting(false);
    };
    
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  // Create printable content as direct HTML
  const createPrintableContent = () => {
    try {
      // Create a new window for printing - simplified for PDF compatibility
      const printWindow = window.open('', '_blank', 'width=800,height=600,menubar=no,toolbar=no');
      
      if (!printWindow) {
        alert("Please allow pop-ups to print this report");
        return;
      }
      
      // Generate current timestamp
      const timestamp = format(new Date(), "MM/dd/yyyy hh:mm:ss a");
      
      // Prepare logo URL with fallback
      const logoUrl = window.location.origin + '/logo.png';
      
      // Generate HTML content
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Vaccination Status Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .report-header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .report-title { flex: 1; }
            .report-logo { text-align: right; }
            .report-logo img { max-height: 60px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 9pt; table-layout: fixed; }
            th, td { border: 1px solid #ddd; padding: 5px; text-align: left; overflow-wrap: break-word; }
            th { background-color: #f2f2f2; }
            .phone-cell { white-space: nowrap; overflow: visible; }
            .email-cell { word-break: break-all; }
            .footer { position: fixed; bottom: 10px; width: 100%; display: flex; justify-content: space-between; font-size: 9pt; color: #666; }
            @page { size: portrait; margin: 15mm; }
            
            /* Critical for print color preservation */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Color coding for same owner - with important flag for printing */
            .color0 { background-color: #e6f7ff !important; }
            .color1 { background-color: #fff1e6 !important; }
            .color2 { background-color: #e6ffe6 !important; }
            .color3 { background-color: #f7e6ff !important; }
            .color4 { background-color: #ffe6e6 !important; }
            .color5 { background-color: #fffde6 !important; }
            .color6 { background-color: #e6fff7 !important; }
            .color7 { background-color: #ffe6f7 !important; }
            .color8 { background-color: #f2f2f2 !important; }
            .color9 { background-color: #e6fffd !important; }
            
            .status-inforce { color: #4caf50 !important; font-weight: bold !important; }
            .status-expired { color: #ff9800 !important; font-weight: bold !important; }
            .status-missing { color: #f44336 !important; font-weight: bold !important; }
          </style>
        </head>
        <body>
          <div class="report-header">
            <div class="report-title">
              <h2>Vaccination Status by Appointment Date</h2>
              <p>${startDate} to ${endDate}</p>
              <p><strong>${vaccinationData.length} records found</strong></p>
            </div>
            <div class="report-logo">
              <img src="${logoUrl}" alt="Company Logo" onerror="this.style.display='none'">
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th width="16%">Customer</th>
                <th width="18%">Phone</th>
                <th width="14%">Email</th>
                <th width="12%">Pet Name</th>
                <th width="10%">Appt Date</th>
                <th width="12%">Vaccination</th>
                <th width="8%">Expires</th>
                <th width="10%">Status</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      // Track colored owners
      const coloredOwners = {};
      let colorIndex = 0;
      
      // Add table rows
      vaccinationData.forEach((record) => {
        const ownerKey = record.email || record.customer_name;
        let colorClass = '';
        
        // Check if this owner has multiple pets
        if (hasMultiplePets(record)) {
          if (!coloredOwners[ownerKey]) {
            coloredOwners[ownerKey] = `color${colorIndex % 10}`;
            colorIndex++;
          }
          colorClass = coloredOwners[ownerKey];
        }
        
        // Get the actual color value based on the class
        const getColorForClass = (classname) => {
          switch(classname) {
            case 'color0': return '#e6f7ff';
            case 'color1': return '#fff1e6';
            case 'color2': return '#e6ffe6';
            case 'color3': return '#f7e6ff';
            case 'color4': return '#ffe6e6';
            case 'color5': return '#fffde6';
            case 'color6': return '#e6fff7';
            case 'color7': return '#ffe6f7';
            case 'color8': return '#f2f2f2';
            case 'color9': return '#e6fffd';
            default: return 'transparent';
          }
        };
        
        // Get background color for this row
        const backgroundColor = colorClass ? getColorForClass(colorClass) : 'transparent';
        
        // Get status color
        let statusColor = '';
        let statusClass = '';
        if (record.STATUS === 'INFORCE') {
          statusColor = '#4caf50';
          statusClass = 'status-inforce';
        } else if (record.STATUS === 'NEED UPDATED EXP DATE') {
          statusColor = '#ff9800';
          statusClass = 'status-expired';
        } else if (record.STATUS === 'NOT ON FILE') {
          statusColor = '#f44336';
          statusClass = 'status-missing';
        }
        
        // Apply both class and inline style for maximum compatibility
        htmlContent += `
          <tr class="${colorClass}" style="background-color: ${backgroundColor} !important;">
            <td style="background-color: ${backgroundColor} !important;">${record.customer_name || ''}</td>
            <td class="phone-cell" style="background-color: ${backgroundColor} !important; white-space: nowrap;">${record.phone || ''}</td>
            <td class="email-cell" style="background-color: ${backgroundColor} !important; word-break: break-all;">${record.email || ''}</td>
            <td style="background-color: ${backgroundColor} !important;">${record.pet_name || ''}</td>
            <td style="background-color: ${backgroundColor} !important;">${formatDate(record.appointment_date)}</td>
            <td style="background-color: ${backgroundColor} !important;">${record.vaccination_type || ''}</td>
            <td style="background-color: ${backgroundColor} !important;">${formatDate(record.vaccine_expiration)}</td>
            <td class="${statusClass}" style="background-color: ${backgroundColor} !important; color: ${statusColor} !important; font-weight: bold;">${record.STATUS || ''}</td>
          </tr>
        `;
      });
      
      // Close the table and add footer
      htmlContent += `
            </tbody>
          </table>
          
          <div class="footer">
            <div>Generated: ${timestamp}</div>
            <div>Page <span class="page-num"></span></div>
          </div>
          
          <script>
            // Simple print without auto-closing for better PDF compatibility
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;
      
      // Write to the new window
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (err) {
      console.error("Error creating print window:", err);
      // Fallback to browser print if window creation fails
      alert("Unable to create print preview. Using browser print instead.");
      window.print();
    }
  };
  
  // Handle printing
  const handlePrint = () => {
    // Ensure data is loaded before printing
    if (!vaccinationData.length && !loading) {
      fetchVaccinationData();
      setTimeout(() => {
        createPrintableContent();
      }, 2000);
    } else {
      createPrintableContent();
    }
  };

  // Fetch vaccination data from API
  const fetchVaccinationData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get token from localStorage
      const token = localStorage.getItem("token") || "dummy-token";

      // Construct API URL with date range parameters
      const apiUrl = `/api/reports/vaccination-status?start_date=${startDate}&end_date=${endDate}&token=${token}`;

      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setVaccinationData(data);
    } catch (err) {
      console.error("Error fetching vaccination data:", err);
      setError(err.message || "Failed to fetch vaccination data");
      setVaccinationData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handler for Run Report button
  const handleRunReport = () => {
    fetchVaccinationData();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return format(date, "MM/dd/yyyy");
    } catch (error) {
      return dateString || "N/A";
    }
  };

  // Get status color for different vaccination statuses
  const getStatusColor = (status) => {
    switch (status) {
      case "INFORCE":
        return "#4caf50"; // Green for valid vaccinations
      case "NEED UPDATED EXP DATE":
        return "#ff9800"; // Orange for expired vaccinations
      case "NOT ON FILE":
        return "#f44336"; // Red for missing vaccinations
      default:
        return "inherit";
    }
  };

  // Process data to identify owners with multiple pets
  const processOwnerData = (data) => {
    // Group data by owner (using email as unique identifier)
    const ownerGroups = {};
    data.forEach(record => {
      const ownerKey = record.email || record.customer_name;
      if (!ownerGroups[ownerKey]) {
        ownerGroups[ownerKey] = [];
      }
      ownerGroups[ownerKey].push(record);
    });

    // Assign colors to owners with multiple pets
    const coloredData = [...data];
    const ownerColors = {};
    const colors = [
      '#e6f7ff', // light blue
      '#fff1e6', // light orange
      '#e6ffe6', // light green
      '#f7e6ff', // light purple
      '#ffe6e6', // light red
      '#fffde6', // light yellow
      '#e6fff7', // light teal
      '#ffe6f7', // light pink
      '#f2f2f2', // light gray
      '#e6fffd', // light cyan
    ];
    
    let colorIndex = 0;
    Object.keys(ownerGroups).forEach(ownerKey => {
      if (ownerGroups[ownerKey].length > 1) {
        ownerColors[ownerKey] = colors[colorIndex % colors.length];
        colorIndex++;
      }
    });
    
    return { coloredData, ownerColors };
  };

  // Current timestamp for footer
  const currentTimestamp = format(new Date(), "MM/dd/yyyy hh:mm:ss a");
  
  // Process data for owner-based color coding
  const { ownerColors } = processOwnerData(vaccinationData);
  
  // Get background color for a row based on owner
  const getRowBackground = (record) => {
    const ownerKey = record.email || record.customer_name;
    return ownerColors[ownerKey] || 'transparent';
  };
  
  // Check if an owner has multiple pets
  const hasMultiplePets = (record) => {
    const ownerKey = record.email || record.customer_name;
    return ownerColors[ownerKey] !== undefined;
  };

  // Force printing all records during print preview
  useEffect(() => {
    if (isPrinting && rowsPerPage !== -1) {
      setRowsPerPage(-1); // Show all records during printing
    }
  }, [isPrinting]);

  return (
    <div>
      {/* Filter Controls - No Print */}
      <Paper className="no-print" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Vaccination Status by Appointment Date
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              id="date-from"
              label="From Date"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              id="date-to"
              label="To Date"
              type="date"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} container spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRunReport}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Run Report"
                )}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePrint}
                disabled={loading || !vaccinationData.length}
                startIcon={<PrintIcon />}
              >
                Print
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Print Area */}
      <div ref={printRef} id="print-container">
        {/* Report Header */}
        <Box sx={{ mb: 3 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item xs>
              <Typography variant="h5" gutterBottom>
                Vaccination Status by Appointment Date
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                {startDate} to {endDate}
              </Typography>
              <Typography variant="subtitle2">
                {vaccinationData.length} records found
              </Typography>
            </Grid>
            <Grid item className="report-logo">
              <img 
                src="/logo.png" 
                alt="Company Logo" 
                style={{ 
                  maxHeight: '60px', 
                  maxWidth: '150px',
                  marginRight: '20px'
                }} 
                onError={(e) => { e.target.style.display = 'none' }}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2 }} />
        </Box>

        {/* Error Message */}
        {error && (
          <Typography color="error" align="center" gutterBottom>
            Error: {error}
          </Typography>
        )}

        {/* Loading Indicator */}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        )}

        {/* Table with Vaccination Data */}
        {!loading && vaccinationData.length > 0 && (
          <>
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                <TableCell width="16%"><strong>Customer</strong></TableCell>
                <TableCell width="16%"><strong>Phone</strong></TableCell>
                <TableCell width="16%"><strong>Email</strong></TableCell>
                    <TableCell width="12%"><strong>Pet Name</strong></TableCell>
                    <TableCell width="10%"><strong>Appt Date</strong></TableCell>
                    <TableCell width="12%"><strong>Vaccination</strong></TableCell>
                    <TableCell width="8%"><strong>Expires</strong></TableCell>
                    <TableCell width="10%"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Show all records when printing, otherwise respect pagination */}
                  {(isPrinting || rowsPerPage === -1
                    ? vaccinationData 
                    : vaccinationData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ).map((record, index) => (
                    <TableRow 
                      key={index}
                      className={hasMultiplePets(record) ? "same-owner-row" : ""}
                      sx={{ 
                        backgroundColor: getRowBackground(record),
                        '&:hover': {
                          backgroundColor: hasMultiplePets(record) 
                            ? `${getRowBackground(record)}!important` 
                            : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {record.customer_name}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {record.phone}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {record.email}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {record.pet_name}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {formatDate(record.appointment_date)}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {record.vaccination_type}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}>
                        {formatDate(record.vaccine_expiration)}
                      </TableCell>
                      <TableCell 
                        sx={{ overflowWrap: 'break-word', padding: 1, whiteSpace: 'normal' }}
                        style={{ color: getStatusColor(record.STATUS) }}
                      >
                        <strong>{record.STATUS}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination - No Print */}
            <TablePagination
              className="no-print"
              rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
              component="div"
              count={vaccinationData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}

        {/* No Data Message */}
        {!loading && vaccinationData.length === 0 && !error && (
          <Typography align="center" color="textSecondary" gutterBottom>
            No vaccination data found for the selected date range.
          </Typography>
        )}

        {/* Footer with timestamp and page number that appears on printed pages */}
        <Box className="page-footer" sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          <Typography variant="caption">
            Generated: {currentTimestamp}
          </Typography>
          <Typography variant="caption" className="page-number">
            Page 
          </Typography>
        </Box>
      </div>
    </div>
  );
};

export default VaccinationReport;